/**
 * BROWSERZUSTAND DES BOARDS (F19, Ruling A5; Muster `panelSpeicher.ts`): Filter, Darstellung
 * (je Spalte | Raster), das Auf-/Zuklappen der Zeitleisten und die zugeklappten Spalten liegen im Browser, nicht in der
 * Datenbank — geräteabhängige Bequemlichkeit. Jeder Wert wird beim Lesen geprüft (er kann
 * fehlen, veraltet oder von Hand verändert sein), jeder Zugriff steht in try/catch (im
 * privaten Fenster wirft schon der Zugriff auf `localStorage`). Scheitert etwas, gilt die
 * Vorgabe. Darstellung und Schreiben laufen über den gemeinsamen Helfer `browserMemory.ts`.
 */
import { readChoice, readJson, rememberValue } from "../../browserMemory.js";

export const ANSICHT_SCHLUESSEL = "ki-tickets-ansicht";
export const FILTER_SCHLUESSEL = "ki-tickets-filter";
export const OFFEN_SCHLUESSEL = "ki-tickets-offen";
export const ZU_SCHLUESSEL = "ki-tickets-zu";

/** `team` (Nachtrag 1e, F134): Kennung eines eigenen Teams oder "" (alle); fehlt = "". Optional, damit gemerkte
 *  Filter von vor 1e gültig bleiben. */
export type BoardFilter = { company: string; customer: string; assignee: string; linked: boolean; team?: string };

/** Die Werte des Filters „Zuständig“, die keine Person nennen (E442): Niemand, Warteschlange meiner Teams, Von mir
 *  delegiert. Eine Quelle für die Leiste (keine erfundene „gewählte Person“) und für die Fremdtickets (kein `?user=`). */
export const SONDER_ZUSTAENDIG: readonly string[] = ["none", "queue", "delegated"];

/** Gemerkt werden nur Kennungen und Schalter, nie Namen von Kunden oder Personen (Review 12):
 *  der Browserspeicher ist kein Ort für Personendaten. Die Namen kommen beim Laden vom Server. */
export const LEERER_FILTER: BoardFilter = { company: "ALLE", customer: "", assignee: "", linked: false, team: "" };

const ANSICHTEN = ["spalten", "raster"] as const;

export function gemerkteAnsicht(): "spalten" | "raster" {
  return readChoice(ANSICHT_SCHLUESSEL, ANSICHTEN, "spalten");
}

export function merkeAnsicht(a: "spalten" | "raster"): void {
  rememberValue(ANSICHT_SCHLUESSEL, a);
}

/**
 * Der gemerkte Filter oder die Vorgabe. Ein Unternehmen, das es nicht (mehr) gibt, fällt auf
 * „Alle" zurück — aber nur, wenn die Unternehmensliste schon da ist (eine leere Liste beim
 * ersten Zeichnen soll die Wahl nicht verwerfen).
 */
export function leseFilter(companyIds: readonly string[]): BoardFilter {
  const w = readJson(FILTER_SCHLUESSEL);
  if (typeof w !== "object" || w === null) return LEERER_FILTER;
  const k = w as Record<string, unknown>;
  if (typeof k.company !== "string" || typeof k.customer !== "string"
      || typeof k.assignee !== "string" || typeof k.linked !== "boolean") return LEERER_FILTER;
  const company = k.company === "ALLE" || companyIds.length === 0 || companyIds.includes(k.company)
    ? k.company : "ALLE";
  return { company, customer: k.customer, assignee: k.assignee, linked: k.linked,
           team: typeof k.team === "string" ? k.team : "" };
}

/** Das Team steht nur im Speicher, wenn es gesetzt ist — leer bleibt der gemerkte Stand wie vor 1e. */
export function merkeFilter(f: BoardFilter): void {
  rememberValue(FILTER_SCHLUESSEL, JSON.stringify({ company: f.company, customer: f.customer,
                                              assignee: f.assignee, linked: f.linked,
                                              ...((f.team ?? "") !== "" ? { team: f.team } : {}) }));
}

/** Schalter je Spalte (Spalten-Id → an/aus); unbrauchbare Einträge fallen weg. */
function leseSchalter(schluessel: string): Record<string, boolean> {
  const w = readJson(schluessel);
  if (typeof w !== "object" || w === null || Array.isArray(w)) return {};
  const schalter: Record<string, boolean> = {};
  for (const [id, wert] of Object.entries(w as Record<string, unknown>)) {
    if (typeof wert === "boolean") schalter[id] = wert;
  }
  return schalter;
}

/** Auf-/Zuklappen der Zeitleiste je Spalte (Spalten-Id → offen: jeder Tag mit Ablagefläche). */
export function leseOffen(): Record<string, boolean> {
  return leseSchalter(OFFEN_SCHLUESSEL);
}

export function merkeOffen(offen: Record<string, boolean>): void {
  rememberValue(OFFEN_SCHLUESSEL, JSON.stringify(offen));
}

/** Zugeklappte Spalten (me-boardspalte `zugeklappt`, 48 px schmal; Spalten-Id → zu). */
export function leseZu(): Record<string, boolean> {
  return leseSchalter(ZU_SCHLUESSEL);
}

export function merkeZu(zu: Record<string, boolean>): void {
  rememberValue(ZU_SCHLUESSEL, JSON.stringify(zu));
}
